import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Eye, ArrowRight, Download, Save, AlertCircle, Clock, CreditCard, Image, UserCheck, FileText, Paperclip, MessageSquare, Globe } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABELS = { pending: "Pendiente", reviewing: "En revisión", approved: "Aprobada", rejected: "Rechazada" };
const STATUS_COLORS = { pending: "bg-[#F1F5F9] text-[#64748B]", reviewing: "bg-[#FEF3C7] text-[#92400E]", approved: "bg-[#DBEAFE] text-[#1E40AF]", rejected: "bg-[#FEE2E2] text-[#991B1B]" };

function isExpired(quote) {
  if (!quote.expires_at) return false;
  return new Date() > new Date(quote.expires_at);
}

function daysLeft(quote) {
  if (!quote.expires_at) return null;
  const diff = new Date(quote.expires_at) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function getAdminState(q) {
  if (q.status === "rejected") return { label: "Rechazada", cls: "bg-[#FEE2E2] text-[#991B1B]", icon: X };
  if (isExpired(q) && q.status === "pending") return { label: "Vencida", cls: "bg-[#FEE2E2] text-[#991B1B]", icon: AlertCircle };
  if (q.status === "pending") return { label: "Nueva", cls: "bg-[#F1F5F9] text-[#64748B]", icon: FileText };
  if (q.status === "reviewing") return { label: "En revisión", cls: "bg-[#FEF3C7] text-[#92400E]", icon: Eye };
  if (q.status === "approved") {
    if (q.payment_verified) return { label: "Pago verificado", cls: "bg-[#D1FAE5] text-[#065F46]", icon: Check };
    if (q.payment_proof_file_id) return { label: "Comprobante recibido", cls: "bg-[#E0E7FF] text-[#3730A3]", icon: Image };
    if (q.client_accepted && q.requires_advance) return { label: "Esperando pago", cls: "bg-[#FEF3C7] text-[#92400E]", icon: CreditCard };
    if (q.client_accepted) return { label: "Cliente aceptó", cls: "bg-[#D1FAE5] text-[#065F46]", icon: UserCheck };
    return { label: "Esperando cliente", cls: "bg-[#DBEAFE] text-[#1E40AF]", icon: Clock };
  }
  return { label: q.status, cls: "bg-[#F1F5F9] text-[#64748B]", icon: FileText };
}

export default function AdminQuotations() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [adminNotes, setAdminNotes] = useState("");
  const [requiresAdvance, setRequiresAdvance] = useState(false);
  const [advancePercent, setAdvancePercent] = useState(50);
  const [confirmedDate, setConfirmedDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchQuotes = () => {
    const url = filterStatus ? `/quotations?status=${filterStatus}` : "/quotations";
    API.get(url).then((r) => { setQuotes(r.data); setLoading(false); }).catch(() => { setQuotes([]); setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchQuotes(); }, [filterStatus]);

  const openQuote = (q) => {
    setSelected(q);
    setEditItems(JSON.parse(JSON.stringify(q.items || [])));
    setAdminNotes(q.admin_notes || "");
    setRequiresAdvance(q.requires_advance || false);
    setAdvancePercent(q.advance_percent || 50);
    setConfirmedDate(q.confirmed_delivery_date || "");
  };

  const updateItemPrice = (idx, field, value) => {
    setEditItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: parseFloat(value) || 0 };
      const item = copy[idx];
      copy[idx].subtotal = Math.round((item.unit_price || 0) * (item.quantity || 0) * 100) / 100;
      return copy;
    });
  };

  const getEditSubtotal = () => editItems.reduce((sum, it) => sum + (it.subtotal || 0), 0);
  const getEditIva = () => Math.round(getEditSubtotal() * 0.12 / 1.17 * 100) / 100;
  const getEditIsr = () => Math.round(getEditSubtotal() * 0.05 / 1.17 * 100) / 100;
  const getEditTotal = () => Math.round(getEditSubtotal() * 100) / 100;

  const handleUpdate = async () => {
    await API.put(`/admin/quotations/${selected.id}`, {
      items: editItems,
      admin_notes: adminNotes,
      requires_advance: requiresAdvance,
      advance_percent: advancePercent,
      confirmed_delivery_date: confirmedDate || null,
    });
    setSelected(null);
    fetchQuotes();
  };

  const updateStatus = async (id, status) => {
    await API.put(`/admin/quotations/${id}`, {
      status,
      items: editItems,
      admin_notes: adminNotes,
      requires_advance: requiresAdvance,
      advance_percent: advancePercent,
      confirmed_delivery_date: confirmedDate || null,
    });
    setSelected(null);
    fetchQuotes();
  };

  const convertToOrder = async (id) => {
    // Save changes first
    await API.put(`/admin/quotations/${id}`, {
      items: editItems,
      admin_notes: adminNotes,
      requires_advance: requiresAdvance,
      advance_percent: advancePercent,
      confirmed_delivery_date: confirmedDate || null,
    });
    await API.post(`/admin/orders/from-quotation/${id}`);
    fetchQuotes();
    setSelected(null);
  };

  const downloadPdf = async (quoteId) => {
    try {
      const response = await API.get(`/quotations/${quoteId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `cotizacion-${quoteId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { if (process.env.NODE_ENV === "development") console.error("PDF download failed", err); }
  };

  const verifyPayment = async (quoteId) => {
    try {
      await API.post(`/admin/quotations/${quoteId}/verify-payment`);
      fetchQuotes();
      setSelected(null);
    } catch (err) { if (process.env.NODE_ENV === "development") console.error("Verify payment failed", err); }
  };

  const deleteQuotation = async (quoteId) => {
    if (!window.confirm("¿Eliminar esta cotización? Esta acción no se puede deshacer.")) return;
    try {
      await API.delete(`/admin/quotations/${quoteId}`);
      fetchQuotes();
      setSelected(null);
    } catch (err) { if (process.env.NODE_ENV === "development") console.error("Delete failed", err); }
  };

  const markAccepted = async (quoteId) => {
    await API.post(`/admin/quotations/${quoteId}/mark-accepted`);
    fetchQuotes();
    setSelected(null);
  };

  const markPaymentReceived = async (quoteId) => {
    await API.post(`/admin/quotations/${quoteId}/mark-payment-received`);
    fetchQuotes();
    setSelected(null);
  };

  const downloadPaymentProof = (fileId) => {
    window.open(`${API_BASE}/api/public/files/${fileId}`, "_blank");
  };

  return (
    <div className="p-6" data-testid="admin-quotations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Gestión</p>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Cotizaciones</h1>
        </div>
        <div className="flex gap-2">
          {["", "pending", "reviewing", "approved", "rejected"].map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${filterStatus === s ? "bg-[#0047AB] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#0047AB]"}`}
              data-testid={`filter-${s || "all"}`}>
              {s ? STATUS_LABELS[s] : "Todas"}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm">
        {loading ? (
          <p className="p-8 text-center text-sm text-[#94A3B8]">Cargando...</p>
        ) : quotes.length === 0 ? (
          <p className="p-8 text-center text-sm text-[#94A3B8]">No hay cotizaciones</p>
        ) : (
          <div className="divide-y divide-[#E2E8F0]" data-testid="quotes-list">
            {quotes.map((q) => {
              const expired = isExpired(q);
              const days = daysLeft(q);
              const state = getAdminState(q);
              const StateIcon = state.icon;
              const total = q.adjusted_total || q.total;

              return (
                <div key={q.id} className={`px-5 py-4 hover:bg-[#F8FAFC] transition-colors cursor-pointer ${expired && q.status === "pending" ? "opacity-50" : ""}`} onClick={() => openQuote(q)} data-testid={`admin-quote-${q.id}`}>
                  <div className="flex items-center gap-4">
                    {/* State icon */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${state.cls}`}>
                      <StateIcon className="w-4 h-4" />
                    </div>

                    {/* Client + Products */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-[#0F172A] truncate">{q.client_name}</p>
                        {q.created_by_admin ? (
                          <span className="flex items-center gap-0.5 text-[9px] font-bold uppercase bg-[#25D366]/15 text-[#128C7E] px-1.5 py-0.5 rounded-sm shrink-0"><MessageSquare className="w-2.5 h-2.5" /> WhatsApp</span>
                        ) : (
                          <span className="flex items-center gap-0.5 text-[9px] font-bold uppercase bg-[#F0F4FF] text-[#0047AB] px-1.5 py-0.5 rounded-sm shrink-0"><Globe className="w-2.5 h-2.5" /> Web</span>
                        )}
                        <span className="text-xs text-[#94A3B8]">·</span>
                        <p className="text-xs text-[#475569] truncate">{q.client_email || q.client_phone}</p>
                      </div>
                      <p className="text-xs text-[#475569] mt-0.5 truncate">{q.items?.map((i) => `${i.product_name} (${i.quantity})`).join(", ")}</p>
                    </div>

                    {/* Status badge */}
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`${state.cls} px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm whitespace-nowrap`}>{state.label}</span>
                      <div className="flex items-center gap-2">
                        {days !== null && !expired && q.status !== "approved" && (
                          <span className="text-[10px] text-[#94A3B8]">{days}d</span>
                        )}
                        {expired && q.status === "pending" && (
                          <span className="text-[10px] text-red-500">Vencida</span>
                        )}
                      </div>
                    </div>

                    {/* Total */}
                    <div className="text-right shrink-0 w-24">
                      <p className="text-base font-bold text-[#0F172A]">Q{total}</p>
                      {q.requires_advance && (
                        <p className="text-[10px] text-[#0047AB]">Ant. {q.advance_percent}%</p>
                      )}
                    </div>

                    {/* Quick indicators */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      {q.payment_proof_file_id && !q.payment_verified && (
                        <div className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" title="Comprobante pendiente de verificar" />
                      )}
                      {q.payment_verified && (
                        <div className="w-2 h-2 rounded-full bg-[#10B981]" title="Pago verificado" />
                      )}
                      <Eye className="w-4 h-4 text-[#CBD5E1]" />
                    </div>
                  </div>

                  {/* Progress indicators for approved quotes */}
                  {q.status === "approved" && (
                    <div className="flex items-center gap-1 mt-2 ml-13 pl-[52px]">
                      <Step done={true} label="Aprobada" />
                      <StepLine done={q.client_accepted} />
                      <Step done={q.client_accepted} label="Aceptada" />
                      <StepLine done={q.payment_proof_file_id} />
                      <Step done={!!q.payment_proof_file_id} label="Comprobante" />
                      <StepLine done={q.payment_verified} />
                      <Step done={q.payment_verified} label="Verificado" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo' }}>Revisar Cotización</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              {/* Client info */}
              <div className="text-sm space-y-1">
                <p><span className="font-medium">Cliente:</span> {selected.client_name} ({selected.client_email})</p>
                <p><span className="font-medium">Teléfono:</span> {selected.client_phone || "N/A"}</p>
                <p><span className="font-medium">NIT:</span> {selected.nit || "CF"}</p>
                {selected.delivery_address && <p><span className="font-medium">Dirección:</span> {selected.delivery_address}</p>}
                <p><span className="font-medium">Estado:</span> {STATUS_LABELS[selected.status]}</p>
                {selected.delivery_date_requested && <p><span className="font-medium">Entrega solicitada:</span> {selected.delivery_date_requested}</p>}
                {isExpired(selected) && selected.status !== "approved" && (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-2 flex items-center gap-2 mt-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs text-red-700 font-medium">Cotización vencida - El cliente debe volver a cotizar</span>
                  </div>
                )}
              </div>

              {/* Client notes */}
              {selected.notes && (
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1">Notas del cliente</p>
                  <p className="text-sm text-[#0F172A]">{selected.notes}</p>
                </div>
              )}

              {/* Design files */}
              {selected.design_file_ids && selected.design_file_ids.length > 0 && (
                <div className="border border-[#0047AB]/20 bg-[#F0F4FF] rounded-sm p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#0047AB] mb-2 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> Archivos de diseño ({selected.design_file_ids.length})
                  </p>
                  <div className="space-y-1">
                    {selected.design_file_ids.map((fileId, i) => (
                      <div key={fileId} className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded-sm px-3 py-2">
                        <span className="text-xs text-[#0F172A]">Archivo {i + 1}</span>
                        <a href={`${API_BASE}/api/public/files/${fileId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0047AB] hover:underline" data-testid={`download-design-${i}`}>
                          <Download className="w-3 h-3" /> Ver / Descargar
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Editable items table */}
              <div className="border border-[#E2E8F0] rounded-sm">
                <div className="px-3 py-2 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#475569]">Productos - Editar precios</span>
                </div>
                <div className="divide-y divide-[#E2E8F0]">
                  {editItems.map((item, i) => (
                    <div key={`${item.product_id}-${item.technique_id}-${i}`} className="px-3 py-3" data-testid={`edit-item-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-sm font-medium text-[#0F172A]">{item.product_name}</span>
                          {item.is_custom && <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm font-medium">PERSONALIZADO</span>}
                        </div>
                        <span className="text-sm font-bold text-[#0F172A]" data-testid={`item-subtotal-${i}`}>Q{(item.subtotal || 0).toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-[#475569] mb-2">{item.technique_name} · {item.quantity} pzas · {item.colors} color(es)</p>
                      {item.custom_description && <p className="text-xs text-[#0047AB] mb-2">Detalle: {item.custom_description}</p>}
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="text-[10px] font-medium text-[#475569] uppercase">Precio Unit.</label>
                          <Input type="number" step="0.01" value={item.unit_price || ""} onChange={(e) => updateItemPrice(i, "unit_price", e.target.value)} className="mt-0.5 h-8 text-sm border-[#CBD5E1] rounded-sm" data-testid={`item-unit-price-${i}`} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-2 bg-[#F8FAFC] border-t border-[#E2E8F0]">
                  <div className="flex justify-between text-sm font-bold text-[#0F172A]">
                    <span>Total (precio al cliente):</span>
                    <span className="text-[#E30613]" data-testid="edit-total">Q{getEditTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
                    <span>IVA incluido (12%): Q{getEditIva().toFixed(2)}</span>
                    <span>ISR incluido (5%): Q{getEditIsr().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Confirmed delivery date */}
              <div>
                <Label className="text-sm font-medium text-[#0F172A]">Fecha de entrega confirmada</Label>
                <Input type="date" value={confirmedDate} onChange={(e) => setConfirmedDate(e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm w-48" data-testid="confirmed-date-input" />
                <p className="text-xs text-[#94A3B8] mt-1">El tiempo de entrega inicia al recibir el anticipo (si aplica)</p>
              </div>

              {/* Advance toggle */}
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">Requiere anticipo</p>
                    <p className="text-xs text-[#475569]">Activar si este trabajo necesita pago anticipado</p>
                  </div>
                  <Switch checked={requiresAdvance} onCheckedChange={setRequiresAdvance} data-testid="advance-toggle" />
                </div>
                {requiresAdvance && (
                  <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                    <div className="flex items-center gap-3">
                      <div>
                        <Label className="text-xs text-[#475569]">Porcentaje</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input type="number" min="1" max="100" value={advancePercent} onChange={(e) => setAdvancePercent(parseInt(e.target.value) || 50)} className="h-8 w-20 text-sm border-[#CBD5E1] rounded-sm" data-testid="advance-percent-input" />
                          <span className="text-sm text-[#475569]">%</span>
                        </div>
                      </div>
                      <div className="text-sm text-[#0F172A]">
                        <p>Anticipo: <span className="font-bold">Q{(getEditTotal() * advancePercent / 100).toFixed(2)}</span></p>
                        <p>Resta: <span className="font-bold">Q{(getEditTotal() * (100 - advancePercent) / 100).toFixed(2)}</span></p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin notes */}
              <div>
                <Label className="text-sm font-medium text-[#0F172A]">Notas para el cliente</Label>
                <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Notas, detalles del precio, tiempos..." className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="admin-notes-input" />
              </div>

              {/* Client acceptance & payment status */}
              {selected.client_accepted && (
                <div className="bg-[#D1FAE5] border border-green-200 rounded-sm p-3">
                  <p className="text-xs font-bold text-green-800 uppercase mb-1">Cliente aceptó la cotización</p>
                  <p className="text-xs text-[#475569]">{selected.client_accepted_at ? new Date(selected.client_accepted_at).toLocaleString() : ""}</p>
                </div>
              )}

              {selected.payment_proof_file_id && (
                <div className={`border rounded-sm p-3 ${selected.payment_verified ? "bg-[#D1FAE5] border-green-200" : "bg-[#FEF3C7] border-amber-200"}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase mb-1" style={{ color: selected.payment_verified ? "#065F46" : "#92400E" }}>
                        {selected.payment_verified ? "Pago verificado" : "Comprobante de pago recibido"}
                      </p>
                      <p className="text-xs text-[#475569]">Archivo: {selected.payment_proof_filename}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadPaymentProof(selected.payment_proof_file_id)} className="rounded-sm text-xs" data-testid="view-payment-proof">
                        Ver comprobante
                      </Button>
                      {!selected.payment_verified && (
                        <Button size="sm" onClick={() => verifyPayment(selected.id)} className="bg-[#10B981] text-white font-bold rounded-sm hover:bg-[#059669] text-xs" data-testid="verify-payment-btn">
                          <Check className="w-3 h-3 mr-1" /> Verificar pago
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* WhatsApp quotation manual actions */}
              {selected.created_by_admin && selected.status === "approved" && (
                <div className="bg-[#25D366]/10 border border-[#25D366]/20 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-[#128C7E]" />
                    <p className="text-sm font-medium text-[#0F172A]">Cotización WhatsApp - Avance manual</p>
                  </div>
                  <p className="text-xs text-[#475569] mb-3">Esta cotización fue creada desde admin. Avanza los pasos manualmente según lo que confirme el cliente por WhatsApp.</p>
                  <div className="flex gap-2 flex-wrap">
                    {!selected.client_accepted && (
                      <Button onClick={() => markAccepted(selected.id)} size="sm" className="bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C] text-xs" data-testid="manual-accept-btn">
                        <UserCheck className="w-3 h-3 mr-1" /> Cliente aceptó
                      </Button>
                    )}
                    {selected.client_accepted && !selected.payment_proof_file_id && selected.requires_advance && (
                      <Button onClick={() => markPaymentReceived(selected.id)} size="sm" className="bg-[#F59E0B] text-white font-bold rounded-sm hover:bg-[#D97706] text-xs" data-testid="manual-payment-btn">
                        <CreditCard className="w-3 h-3 mr-1" /> Pago recibido
                      </Button>
                    )}
                    {selected.client_accepted && selected.payment_proof_file_id && !selected.payment_verified && (
                      <Button onClick={() => verifyPayment(selected.id)} size="sm" className="bg-[#10B981] text-white font-bold rounded-sm hover:bg-[#059669] text-xs" data-testid="manual-verify-btn">
                        <Check className="w-3 h-3 mr-1" /> Verificar pago
                      </Button>
                    )}
                    {selected.client_accepted && (!selected.requires_advance || selected.payment_verified) && (
                      <Button onClick={() => convertToOrder(selected.id)} size="sm" className="bg-[#10B981] text-white font-bold rounded-sm hover:bg-[#059669] text-xs" data-testid="manual-order-btn">
                        <ArrowRight className="w-3 h-3 mr-1" /> Convertir a Pedido
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button onClick={handleUpdate} variant="outline" className="border-[#475569] text-[#475569] rounded-sm hover:bg-[#F1F5F9]" data-testid="update-quote-btn">
                  <Save className="w-4 h-4 mr-1" /> Actualizar
                </Button>
                <Button onClick={() => downloadPdf(selected.id)} variant="outline" className="border-[#0047AB] text-[#0047AB] rounded-sm hover:bg-[#F0F4FF]" data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 mr-1" /> Descargar PDF
                </Button>
                {selected.status === "pending" && (
                  <Button onClick={() => updateStatus(selected.id, "reviewing")} className="bg-[#0047AB] text-white font-bold rounded-sm hover:bg-[#003A8C]" data-testid="mark-reviewing-btn">
                    <Eye className="w-4 h-4 mr-1" /> En Revisión
                  </Button>
                )}
                {(selected.status === "pending" || selected.status === "reviewing") && (
                  <>
                    <Button onClick={() => convertToOrder(selected.id)} className="bg-[#10B981] text-white font-bold rounded-sm hover:bg-[#059669]" data-testid="convert-order-btn">
                      <ArrowRight className="w-4 h-4 mr-1" /> Convertir a Pedido
                    </Button>
                    <Button onClick={() => updateStatus(selected.id, "rejected")} variant="outline" className="border-red-300 text-red-600 rounded-sm hover:bg-red-50" data-testid="reject-quote-btn">
                      <X className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  </>
                )}
                <Button onClick={() => deleteQuotation(selected.id)} variant="outline" className="border-red-400 text-red-500 rounded-sm hover:bg-red-50 ml-auto" data-testid="delete-quote-btn">
                  <X className="w-4 h-4 mr-1" /> Eliminar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Step({ done, label }) {
  return (
    <div className="flex items-center gap-1">
      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${done ? "bg-[#0047AB]" : "bg-[#E2E8F0]"}`}>
        {done && <Check className="w-2.5 h-2.5 text-white" />}
      </div>
      <span className={`text-[9px] font-medium ${done ? "text-[#0047AB]" : "text-[#94A3B8]"}`}>{label}</span>
    </div>
  );
}

function StepLine({ done }) {
  return <div className={`w-6 h-0.5 ${done ? "bg-[#0047AB]" : "bg-[#E2E8F0]"}`} />;
}
